function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    console.log(button);

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      console.log(modal);
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver(entries => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    this.sliderControlLinksArray = Array.from(this.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(element => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(new CustomEvent('slideChanged', { detail: {
        currentPage: this.currentPage,
        currentElement: this.sliderItemsToShow[this.currentPage - 1]
      }}));
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach(link => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1]?.classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1]?.setAttribute('aria-current', true);
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (element.offsetLeft + element.clientWidth) <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + (step * this.sliderItemOffset) : this.slider.scrollLeft - (step * this.sliderItemOffset);
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }
  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft + this.sliderItemOffset * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
        console.log(scrollLeft);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);


      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
      // Find the size-variant within the custom element and add class
      const sizeVariant = this.querySelector(".size-variant");
      if (sizeVariant) {
        setTimeout(() => sizeVariant.classList.add("remove-fixed"), 0);
        // sizeVariant.classList.add("remove-fixed");
        requestAnimationFrame(() => sizeVariant.offsetHeight); // Force reflow
      }

    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;
    console.log(target);
    

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
      const variantId = target.getAttribute("data-option-value-id");
      const productPage = document.querySelector('.product-page');
      const template = productPage?.getAttribute('data-template');
      if (template !== 'gift_card') {
      const variantId = target.getAttribute("data-option-value-id");
      document.querySelector(".sticky_buy_button .product-variant-id")?.setAttribute("value", variantId);
      
      if(target.classList.contains("disabled")){
      document.querySelector(".sticky_buy_button .product-form__submit")?.setAttribute("disabled", "disabled");
      document.querySelector(".sticky_buy_button .product-form__submit span").innerHTML = "sold out";
      }
      else{
        document.querySelector(".sticky_buy_button .product-form__submit")?.removeAttribute("disabled", "disabled");
        document.querySelector(".sticky_buy_button .product-form__submit span").innerHTML = "Add To Cart";
      }
      document.querySelectorAll(".product-form-variants-sticky .size-variants .size-variant input").forEach(Id =>{
        let id = Id.getAttribute("data-option-value-id");
        if(id == variantId)
        {
          Id.classList.add("checked");
        }
        else{
          Id.classList.remove("checked");
        }
     });
    }
   }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}


document.querySelector(".menu-drawer__navigation-container .menu-drawer__close").addEventListener("click", function(){
  console.log(".close_clicked");
 document.querySelector("body").classList.remove("overflow-hidden-tablet");
 document.querySelector("header-drawer").classList.remove("menu-opening");
 document.querySelector("header-drawer .menu-drawer-container").removeAttribute("open");
 document.querySelector("header-drawer .menu-drawer-container .header__icon").setAttribute("aria-expanded", false);
});


document.addEventListener("DOMContentLoaded", () => {
  const tabs = document.querySelectorAll(".tab-navigation .tab");
  const tabContents = document.querySelectorAll(".tab-content");
  const banners = document.querySelectorAll(".customer-account-banner");
  const accountTabs = document.querySelectorAll(".account-tabs");
  accountTabs.forEach(accountTab => {
    accountTab.addEventListener("click", () => {
      accountTabs.forEach(tab => tab.classList.remove("active"));
      tabContents.forEach(content => content.style.display = "none");
      banners.forEach(banner => banner.style.display = "none");
      const activeTabId = accountTab.id;
      const activeTabContent = document.getElementById(`tabcontent-${activeTabId}`);
      if (activeTabContent) {
        activeTabContent.style.display = "block"; // Show the active tab's content
      }
      accountTab.classList.add("active"); // Mark this tab as active
      tabs.forEach(mainTab => {
        mainTab.classList.remove("active");
        if (mainTab.id === activeTabId) {
          mainTab.classList.add("active"); // Add active class to the "account" tab
        }
      });
      const activeBanner = document.getElementById(`${activeTabId}-banner`);
      if (activeBanner) {
        activeBanner.style.display = (activeTabId === 'account') ? "block" : "flex"; // Display the banner
      }
    });
  });

  // Handling clicks on main tabs (like account, wishlist, etc.)
  tabs.forEach(tab => {
    tab.addEventListener("click", () => {
      tabs.forEach(t => t.classList.remove("active"));
      tabContents.forEach(content => content.style.display = "none");
      banners.forEach(banner => banner.style.display = "none");
      const activeTabId = tab.id;
      const activeTabContent = document.getElementById(`tabcontent-${activeTabId}`);
      if (activeTabContent) {
        activeTabContent.style.display = "block"; // Show the active tab's content
      }
      const activeBanner = document.getElementById(`${activeTabId}-banner`);
      if (activeBanner) {
        activeBanner.style.display = (activeTabId === 'account') ? "block" : "flex"; // Display the banner
      }
      tab.classList.add("active");
    });
  });

  // Optionally, trigger a click on the first tab to display it by default
  if (tabs.length > 0) {
    tabs[0].click(); // Simulate a click on the first tab to show its content and banner
  }
});


// const infoWrapper = document.querySelector('.product__info-wrapper');
// const productVariant = document.querySelector('.product-form__input--pill.size-variant');
// const productForm = document.querySelector('.product-form');
// const onLoadSticky = document.querySelector(".onload-sticky");

// window.addEventListener('scroll', () => {
//   const scrollPosition = window.scrollY;
//   const targetPosition = infoWrapper?.offsetTop - 150; // Adjust offset as needed
//   const elements = document.querySelectorAll('.product__info-wrapper.grid__item');  
//   if(productForm  ){
//   const targetStickyPosition = productForm?.offsetTop + 300;
//   if (scrollPosition >= targetStickyPosition + 50) {
//     productForm.classList.add('sticky-buton');
//   } else {
//     productForm.classList.remove('sticky-buton');
//   }
// }
// });

const stickyButton = document.querySelector(".product-form-variants-sticky");
const nonStickyButton = document.querySelector(".product-form__buttons");
const nonStickyForm = document.querySelector("product-form.product-form.non-sticky");
const wishlistBtn = document.querySelector(".swym-wishlist-button-bar");
function toggleStickyButton() {
    if (nonStickyButton) {
        const rect = nonStickyButton.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) {
            stickyButton.classList.remove("remove-sticky");
            wishlistBtn.classList.remove('remove-sticky');
        } else {
            stickyButton.classList.add("remove-sticky");
            wishlistBtn.classList.add('remove-sticky');
        }
    }
}

// Check on scroll and resize
window.addEventListener("scroll", toggleStickyButton);

// Initial check
toggleStickyButton();


    const sliderButtons = document.querySelector(".product__media-wrapper .slider-buttons");
    function checkViewportHeight() {
        const viewportHeight = window.innerHeight;
        console.log("Viewport height on load:", viewportHeight);

        // Apply class if viewport height is greater than 650px
        if (viewportHeight < 670) {
            sliderButtons?.classList?.add("onload-sticky");
        } else {
            sliderButtons?.classList?.remove("onload-sticky");
        }
    }

        function handleScroll() {
        if (window.scrollY > 10) {
            sliderButtons?.classList?.remove("onload-sticky");
        } else {
            sliderButtons?.classList?.add("onload-sticky");
        }
    }

    // Run check on load
    setTimeout(checkViewportHeight, 300);

    // Toggle class on scroll (removes when scrolling, adds back when returning to top)
    window.addEventListener("scroll", handleScroll);

    // Handle viewport height changes on resize
    window.addEventListener("resize", function () {
        setTimeout(checkViewportHeight, 300);
    });

document.addEventListener("click", function (event) {
  // Check if the clicked element is an "Add to Cart" button
  if (event.target.matches(".cart-add-to-cart-form .button")) {
    event.preventDefault();
    console.log("clicked");

    // Hide the button and show the option-values-wrapper
    let addToCartBtn = event.target;
    addToCartBtn.style.display = "none";

   let cardWrapper = addToCartBtn.closest(".card-wrapper");
    let optionValuesWrapper = cardWrapper.querySelectorAll(".option-values-wrapper");
    optionValuesWrapper.forEach(option => {
      if (option) {
        option.style.display = "block";
      }
    });
  }

  // Check if the clicked element is an option value
  if (event.target.matches(".option-value")) {
    let option = event.target;
    let dataVariantId = option.getAttribute("data-variant-id");
    console.log("Selected Variant ID:", dataVariantId);

      let cardWrapper = option.closest(".card-wrapper");
    let addToCartForm = cardWrapper.querySelectorAll(".cart-add-to-cart-form");
    addToCartForm.forEach(form => {
    if (form) {
      form.querySelector("input[name='id']").setAttribute("value", dataVariantId);
    }
  })
    let cartDrawer = document.querySelector("cart-drawer");
    cartDrawer.classList.add("active");

    let formData = {
      items: [
        {
          id: dataVariantId,
          quantity: 1,
        },
      ],
    };

    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "product-form",
          productVariantId: dataVariantId,
          cartData: response,
        });

        if (cartDrawer.classList.contains("is-empty")) {
          cartDrawer.classList.remove("is-empty");
          cartDrawer.querySelector("button#CartDrawer-Checkout").removeAttribute("disabled");
          cartDrawer.querySelector(".drawer__inner-empty").style.display = "none";
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }
});


function sendUpdatedCartData(eventType = 'view_cart') {
  // Step 1: Fetch the updated cart section HTML with current cart items
  fetch('/cart?section_id=cart-product-data')
    .then(res => res.text())
    .then(html => {
      // Step 2: Parse the returned HTML string
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Step 3: Locate the script tag containing JSON data
      const scriptTag = doc.getElementById('cart-product-data');

      if (scriptTag) {
        // Step 4: Parse the JSON data inside the script tag
        const productData = JSON.parse(scriptTag.textContent);

        // Step 5: Push to Data Layer for analytics (e.g., GA4)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce data
        window.dataLayer.push({
          event: eventType,
          ecommerce: {
            items: productData.products
          }
        });

        console.log('✅ Data Layer updated with:', productData.products);
      } else {
        console.error('⚠️ Could not find cart-product-data script tag in section HTML');
      }
    })
    .catch(error => {
      console.error('❌ Error fetching updated cart product data:', error);
    });
}




(function () {
  let isHandlingCartOpen = false;

  const attachUpcartHook = () => {
    if (typeof window.upcartOnCartOpened === 'function') {
      const original = window.upcartOnCartOpened;

      window.upcartOnCartOpened = () => {
        if (!isHandlingCartOpen) {
          isHandlingCartOpen = true;

          setTimeout(() => {
            sendUpdatedCartData("view_cart");

            // Allow it to trigger again on the next cart open
            setTimeout(() => {
              isHandlingCartOpen = false;
            }, 500); // small buffer to debounce extra triggers
          }, 7000); // wait for cart to load
        }

        // Call original hook
        original();
      };
    } else {
      // Retry until UpCart defines the hook
      setTimeout(attachUpcartHook, 100);
    }
  };

  attachUpcartHook();
})();


  window.upcartOnAddUpsell = (id) => {
    console.log(id);
    handleAddToCartClick();
  }

  window.upcartOnAddToCart = (id, quality, item) => {
    sendAddCartData(item.variant_id, "add_to_cart");
  }

  function sendAddCartData(variantId, eventType) {
    setTimeout(() => {
      console.log("Triggered sendAddCartData after delay:", variantId);
      
      const dataScript = document.querySelector('#product-variant-data');
      if (!dataScript) {
        console.warn("product-variant-data script not found in DOM.");
        return;
      }
  
      const productData = JSON.parse(dataScript.textContent);
      const productInfo = productData.products.find(p => Number(p.variant_id) === Number(variantId));
  
      if (!productInfo) {
        console.warn("Product info not found for variant ID:", variantId);
        return;
      }
  
      const item = {
        item_variant_id: productInfo.variant_id,
        item_name: productInfo.item_name,
        item_id: productInfo.item_id,
        item_sku:productInfo.item_sku,
        item_sku:productInfo.item_sku,
        item_category: productInfo.item_category,
        item_category2: productInfo.item_category2,
        discount_percentage: productInfo.discount_percentage,
        size: productInfo.option_1 || null,
        color: productInfo.option_2 || null,
        stock_status: productInfo.stock_status,
        price: productInfo.price,
        currency: productInfo.currency,
        quantity: 1
      };
  
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: eventType,
        ecommerce: {
          items: [item]
        }
      });
  
      console.log("GA4 event pushed:", eventType, item);
    }, 500); // Delay of 500ms (can be adjusted)
  }

  function setupButtonListeners(shadowRoot) {
    // This function will apply listeners to the plus and minus buttons
    const applyListeners = () => {
      const upcartPlusButtons = shadowRoot.querySelectorAll('.upcart-product-quantity-plus');
      const upcartMinusButtons = shadowRoot.querySelectorAll('.upcart-product-quantity-minus');
      const upcartDeleteButons = shadowRoot.querySelectorAll('.styles_TrashButton__deleteButton__')
  
      upcartPlusButtons.forEach(button => {
        button.removeEventListener("click", handleAddClick);
        button.addEventListener("click", handleAddClick);
      });
  
      upcartMinusButtons.forEach(button => {
        button.removeEventListener("click", handleRemoveClick);
        button.addEventListener("click", handleRemoveClick);
      });
      
      upcartDeleteButons.forEach(button => {
        button.removeEventListener("click", handleRemoveIconClick);
        button.addEventListener("click", handleRemoveIconClick);
      });
    };
  
    // Set up a mutation observer to monitor changes within the shadow DOM
    const innerObserver = new MutationObserver(() => {
      applyListeners(); // Apply listeners each time there’s a change
    });
  
    // Start observing the shadowRoot for childList changes, which catches re-renders
    innerObserver.observe(shadowRoot, { childList: true, subtree: true });
  
    // Apply listeners initially
    applyListeners();
  }

  function handleAddClick(button) {
    console.log("Add to cart clicked");
    const productLink = this.closest(".upcart-product-item").querySelector(".upcart-product-title-link").getAttribute("href");
    const [path, query] = productLink.split("?");
    const productHandle = path.replace("/products/", "");
    const variantId = new URLSearchParams(query).get("variant");
    pushToDataLayer(productHandle, variantId, "add_to_cart", "1");
    }
    
    function handleRemoveClick(button) {
    console.log("Remove from cart clicked");
    const productLink = this.closest(".upcart-product-item").querySelector(".upcart-product-title-link").getAttribute("href");
    const [path, query] = productLink.split("?");
    const productHandle = path.replace("/products/", "");
    const variantId = new URLSearchParams(query).get("variant");
    pushToDataLayer(productHandle, variantId, "remove_from_cart", "1");
    }
    
    function handleRemoveIconClick(){
      console.log("Remove from cart clicked");
      let quantity = this.closest(".Stack__item--fill").querySelector(".upcart-product-quantity-input").value;
      const productLink = this.closest(".upcart-product-item").querySelector(".upcart-product-title-link").getAttribute("href");
      const [path, query] = productLink.split("?");
      const productHandle = path.replace("/products/", "");
      const variantId = new URLSearchParams(query).get("variant");
      pushToDataLayer(productHandle, variantId, "remove_from_cart", quantity);
    }


  // Function to get a single product by handle
  async function getProductByHandle({ handle }) {
    let queryResult;
    // Assuming this function returns the single product object using the handle
    const query = `query ProductByHandle($handle: String!) {
      productByHandle(handle: $handle) {
        id
        title
        tags
        priceRange {
          maxVariantPrice {
            amount
            currencyCode
          }
          minVariantPrice {
            amount
            currencyCode
          }
        }
        collections(first: 100) {
          edges {
            node {
              title
              description
              handle
            }
          }
        }
        variants(first: 100) {
          edges {
            node {
              id
              title
              availableForSale
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              selectedOptions {
                name
                value
              }
              sku
            }
          }
        }
      }
    }`;
    
    const variables = { handle };
    const body = JSON.stringify({ query, variables });
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": "a171c51a2bc2b1ca2591a944f4d3a0f3", // Replace with your actual token
      },
      body: body,
    };
    
    

    try {
      const response = await fetch("https://bananhot.com/api/2024-10/graphql.json", options);
      const data = await response.json();
      console.log(data);
      return data?.data?.productByHandle ? [data.data.productByHandle] : [];
    } catch (error) {
      console.error("Fetch error:", error);
    }

    }
  
    function getItemCategories({ product }) {
      let item_category1 = 'Unknown';
      let item_category2 = 'Unknown';
  
  for (let collection of product.collections.edges) {
      if (collection.node.title.includes('Swimwear') || collection.node.title.includes('swimwear')) {
          item_category1 = 'Swimwear';
          if (product.tags.includes('Category_Top')) {
              item_category2 = 'bikini tops';
          } else if (product.tags.includes('Category_Bottom')) {
              item_category2 = 'bikini bottoms';
          } else if (product.tags.includes('Category_One Piece')) {
              item_category2 = 'one pieces';
          } else if (product.tags.includes('Category_Men_Swimwear')) {
              item_category2 = 'men’s swimwear';
          }
      } else if (collection.node.title.includes('Activewear') || collection.node.title.includes('activewear')) {
          item_category1 = 'Activewear';
          if (product.tags.includes('Category_ACTIVE TOP')) {
              item_category2 = 'tops';
          } else if (product.tags.includes('Category_LEGGING')) {
              item_category2 = 'leggings';
          } else if (product.tags.includes('Category_ONESIE')) {
              item_category2 = 'onesies';
          }
      } else if (collection.node.title.includes('Beachwear') || collection.node.title.includes('beachwear')) {
          item_category1 = 'Beachwear';
          if (product.tags.includes('Category_Sarong')) {
              item_category2 = 'sarongs';
          } else if (product.tags.includes('Category_Dress')) {
              item_category2 = 'dresses';
          } else if (product.tags.includes('Category_Skirt')) {
              item_category2 = 'beach skirts';
          } else if (product.tags.includes('Category_Pants')) {
              item_category2 = 'pants';
          } else if (product.tags.includes('Category_Cover-Up')) {
              item_category2 = 'cover-ups';
          } else if (product.tags.includes('Category_Men_Beachwear')) {
              item_category2 = 'men’s beachwear';
          }
      } else if (collection.node.title.includes('Accessories') || collection.node.title.includes('accessories')) {
          item_category1 = 'Accessories';
          if (product.tags.includes('Category_accessories')) {
              item_category2 = 'active accessories';
          } else if (product.tags.includes('Category_jewelry')) {
              item_category2 = 'jewelry';
          } else if (product.tags.includes('Category_Hats')) {
              item_category2 = 'hats';
          } else if (product.tags.includes('Category_Slides')) {
              item_category2 = 'slides';
          }
      }
  }
  return {item_category1, item_category2};  
}

      
      function calculateDiscountPercentage(price, compareAtPrice) {
      if (compareAtPrice <= price || compareAtPrice <= 0) {
        return 0;
      }
      
      
      // Matching Liquid's arithmetic
      const discountAmount = compareAtPrice - price;
      const discountPercentage = (discountAmount * 100) / compareAtPrice;
      
      // Use Math.floor to round down to the nearest integer, similar to how Liquid rounds
      return Math.floor(discountPercentage);
      }
      
  // Function to handle a single product and push it to the dataLayer
  async function pushToDataLayer(handle, variantId, eventype, quantity) {
    try {
      let products = await getProductByHandle({ handle }); // Get the product by handle
      if (products.length) {
        const product = products[0]; 
    const selectedVariant = product.variants.edges.find(
      (edge) => edge.node.id === `gid://shopify/ProductVariant/${variantId}`
    )?.node;
    
    if (!selectedVariant) {
      throw new Error("Variant not found");
    }
        // Extract categories and other product-related properties
        const { item_category1, item_category2 } = getItemCategories({ product });
    
        const discount_percentage = selectedVariant.compareAtPrice?.amount
      ? calculateDiscountPercentage(selectedVariant.price.amount, selectedVariant.compareAtPrice.amount)
      : 0;
    
        //Extract selected options (if any)
        const options = selectedVariant.selectedOptions ? selectedVariant.selectedOptions.reduce((acc, obj) => {
          acc[obj.name.toLowerCase()] = obj.value;
          return acc;
        }, {}) : {};
    
        const fullItemId = product.id;
        const itemId = fullItemId.split("/").pop();
        // Build the item object for dataLayer
        const item = {
          ...options,
          item_name: product.title,
          item_id: itemId, // Parent product ID
          item_variant_id: variantId, // Variant ID
          item_sku: selectedVariant.sku,
          item_category: item_category1,
          item_category2: item_category2,
          discount_percentage: Math.round(discount_percentage) + "%",
          stock_status: selectedVariant.availableForSale ? "In stock" : "Out of stock",
          price: String(selectedVariant.price.amount),
          currency: selectedVariant.price.currencyCode,
          quantity: quantity // Assuming quantity is 1 for a single product
        };
    
        // Push the item to the dataLayer
        dataLayer.push({ ecommerce: null }); // Clear existing ecommerce data
        dataLayer.push({
          event: eventype,
          ecommerce: {
            items: [item] // Add the single item to the ecommerce array
          }
        });
      }
    } catch (error) {
      console.error("Error pushing to dataLayer:", error);
    }
    }
  
 // Define event handler functions separately
function handleNextClick(shadowRoot) {
  return function () {
    const selectedSlide = shadowRoot.querySelector(".slide.selected");
    if (selectedSlide) {
      const nextSlide = selectedSlide.nextElementSibling;
      if (nextSlide) {
        const productLinkNext = nextSlide.querySelector(".upcart-upsell-item-image-wrapper a");
        const productHandleNext = productLinkNext.getAttribute('href').split("?")[0].replace("/products/", "");
        const variantSelector = nextSlide.querySelector(".upcart-upsell-item-variant-selector");
        const selectedOption = variantSelector.options[variantSelector.selectedIndex];
        const productId = selectedOption ? selectedOption.getAttribute("value") : null;
        pushToDataLayer(productHandleNext, productId, "upcart_upsell_viewed", "1");
      } else {
        console.log("No next slide available");
      }
    } else {
      console.error("Selected slide not found");
    }
  };
}

function handlePrevClick(shadowRoot) {
  return function () {
    const selectedSlide = shadowRoot.querySelector(".slide.selected");
    if (selectedSlide) {
      const prevSlide = selectedSlide.previousElementSibling;
      if (prevSlide) {
        const productLinkPrev = prevSlide.querySelector(".upcart-upsell-item-image-wrapper a");
        const productHandlePrev = productLinkPrev.getAttribute('href').split("?")[0].replace("/products/", "");
        const variantSelector = prevSlide.querySelector(".upcart-upsell-item-variant-selector");
        const selectedOption = variantSelector.options[variantSelector.selectedIndex];
        const productId = selectedOption ? selectedOption.getAttribute("value") : null;
        pushToDataLayer(productHandlePrev, productId, "upcart_upsell_viewed", "1");
      } else {
        console.log("No previous slide available");
      }
    } else {
      console.error("Selected slide not found");
    }
  };
}

function handleAddToCartClick(event) {
  const customElement = document.querySelector('#upCart');
  const shadowRoot = customElement.shadowRoot; // Replace with actual shadow root selector if needed
  console.log(shadowRoot);
  const selectedSlide = shadowRoot.querySelector(".slide.selected");
  if (selectedSlide) {
    const productLink = selectedSlide.querySelector(".upcart-upsell-item-image-wrapper a");
    const productHandle = productLink.getAttribute('href').split("?")[0].replace("/products/", "");
    const variantSelector = selectedSlide.querySelector(".upcart-upsell-item-variant-selector");
    const selectedOption = variantSelector.options[variantSelector.selectedIndex];
    const productId = selectedOption ? selectedOption.getAttribute("value") : null;

    pushToDataLayer(productHandle, productId, "upcart_upsell_added", "1");
  } else {
    console.error("No selected slide found.");
  }
}


// Setup listeners for carousel buttons and use delegation for Add to Cart
function setupCarouselListeners(shadowRoot) {
  const upsellCarouselNextBtn = shadowRoot.querySelector("button.control-arrow.control-next");
  const upsellCarouselPrevBtn = shadowRoot.querySelector("button.control-arrow.control-prev");

  // Add click listeners for Next and Previous buttons
  upsellCarouselNextBtn?.addEventListener("click", handleNextClick(shadowRoot));
  upsellCarouselPrevBtn?.addEventListener("click", handlePrevClick(shadowRoot));

}
 
  
  // Watch for upCart shadow DOM element to appear
  const observer = new MutationObserver(() => {
  const customElement = document.querySelector('#upCart');
  if (customElement && customElement.shadowRoot) {
    setupButtonListeners(customElement.shadowRoot);
    setupCarouselListeners(customElement.shadowRoot);
    observer.disconnect(); // Stop observing once the element is found
  }
  });

  
  // Start observing the body for changes (to catch dynamically added #upCart)
  observer.observe(document.body, { childList: true, subtree: true });

let url = window.location.href;

if (url.includes('/collections/') && url.includes('/products/')) {
  const urlParts = url.split('/');
  const collectionNameIndex = urlParts.indexOf('collections') + 1;

  if (collectionNameIndex > 0 && urlParts[collectionNameIndex]) {
    let collectionName = decodeURIComponent(urlParts[collectionNameIndex].replace(/-/g, ' '));
    let collectionTitle = document.querySelector(".collection-title");

    if (collectionTitle) {
      collectionTitle.innerHTML = collectionName;
    }
  }
}



  const addressForm = document.querySelector('form[action="/account/addresses"]');
  const tabs = document.querySelectorAll('.tab-navigation .tab');
  const addressBookTab = document.querySelector('#address-book');

  if (addressForm) {
    addressForm.addEventListener('submit', function (event) {
      event.preventDefault(); // Prevent default submission
      
      const formData = new FormData(addressForm);

      // Send the form data using fetch API
      fetch(addressForm.action, {
        method: 'POST',
        body: formData,
      })
        .then(response => {
          if (response.ok) {
            // Redirect to the account page after successful submission
            setTimeout(() => {
            window.location.href = '/account';
            console.log(tabs);
            tabs.forEach(tab => tab.classList.remove('active')); // Remove active from all tabs
            if (addressBookTab) {
              addressBookTab.classList.add('active'); // Add active to Address book tab
            }
          }, 1000);
          } else {
            console.error('Form submission failed');
          }
        })
        .catch(error => console.error('Error:', error));
    });
  }

  document.querySelectorAll('.facets-layout-list--swatch .mobile-facets__item [data-swatch]').forEach(function(swatch) {
    const colorName = swatch.getAttribute('data-swatch').toLowerCase().trim();
    if (window.colorMap[colorName]) {
      swatch.querySelector(".swatch").style.backgroundColor = window.colorMap[colorName];
    }
  });